/**
 * PDF Form Filler Utility
 * This utility handles PDF form filling using pdf-lib
 */

import { PDFDocument } from "pdf-lib";

class PDFFormFiller {
  /**
   * Load a PDF from a File object
   * @param {File} file - The PDF file
   * @returns {Promise<{pdfDoc: PDFDocument, formFields: Object}>} - The loaded PDF document and form fields
   */
  static async loadPDF(file) {
    try {
      // Read the file as an ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();

      // Load the PDF document
      const pdfDoc = await PDFDocument.load(arrayBuffer);

      // Get form fields
      const form = pdfDoc.getForm();
      const fields = form.getFields();
      const fieldNames = fields.map((field) => field.getName());

      // Create an object with field names and their current values
      const formFields = {};
      fieldNames.forEach((name) => {
        try {
          const field = form.getTextField(name);
          if (field) {
            formFields[name] = field.getText() || "";
          }
        } catch (error) {
          // Field might not be a text field
          try {
            const field = form.getCheckBox(name);
            if (field) {
              formFields[name] = field.isChecked();
            }
          } catch (innerError) {
            // Not a text field or checkbox
            formFields[name] = "";
          }
        }
      });

      return { pdfDoc, formFields, fieldNames };
    } catch (error) {
      console.error("Error loading PDF:", error);
      throw new Error(`Failed to load PDF: ${error.message}`);
    }
  }

  /**
   * Fill PDF form fields
   * @param {PDFDocument} pdfDoc - The PDF document
   * @param {Object} fieldValues - Object with field names and values
   * @returns {Promise<PDFDocument>} - The filled PDF document
   */
  static async fillFormFields(pdfDoc, fieldValues) {
    try {
      const form = pdfDoc.getForm();
      // Update form fields
      Object.entries(fieldValues).forEach(([fieldName, value]) => {
        try {
          const field = form.getTextField(fieldName);
          if (field) {
            field.setText(value.toString());
          }
        } catch (error) {
          // Field might not be a text field
          try {
            const field = form.getCheckBox(fieldName);
            if (field) {
              if (value) {
                field.check();
                field.defaultUpdateAppearances();
              } else {
                field.uncheck();
              }
            }
          } catch (innerError) {
            // Not a text field or checkbox
            console.warn(`Could not set value for field: ${fieldName}`);
          }
        }
      });

      return pdfDoc;
    } catch (error) {
      console.error("Error filling PDF form:", error);
      throw new Error(`Failed to fill PDF form: ${error.message}`);
    }
  }

  /**
   * Save a PDF document
   * @param {PDFDocument} pdfDoc - The PDF document
   * @param {string} filename - The filename to save as
   * @returns {Promise<Blob>} - The PDF as a Blob
   */
  static async savePDF(pdfDoc, filename = "filled-form.pdf") {
    try {
      // Save the PDF
      const pdfBytes = await pdfDoc.save();

      // Convert to Blob
      const blob = new Blob([pdfBytes], { type: "application/pdf" });

      // Create download link
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();

      return blob;
    } catch (error) {
      console.error("Error saving PDF:", error);
      throw new Error(`Failed to save PDF: ${error.message}`);
    }
  }

  /**
   * Process a PDF - load, fill, and optionally save
   * @param {File} file - The PDF file
   * @param {Object} fieldValues - Object with field names and values
   * @param {boolean} save - Whether to save the PDF
   * @param {string} filename - The filename to save as
   * @returns {Promise<{blob: Blob, url: string}>} - The processed PDF
   */
  static async processPDF(
    file,
    fieldValues,
    save = true,
    filename = "filled-form.pdf"
  ) {
    try {
      // Load the PDF
      const { pdfDoc } = await this.loadPDF(file);

      // Fill the form fields
      const filledPdfDoc = await this.fillFormFields(pdfDoc, fieldValues);

      // Save the PDF
      const pdfBytes = await filledPdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      if (save) {
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.click();
      }

      return { blob, url };
    } catch (error) {
      console.error("Error processing PDF:", error);
      throw new Error(`Failed to process PDF: ${error.message}`);
    }
  }
}

export default PDFFormFiller;
